import type { WorkflowKnowledgeContextItem } from '@/utils/workflowKnowledge'

import type { KnowledgeSearchResult, StoryboardSceneDraft } from './types'

export function getKnowledgeContextKey(item: Pick<WorkflowKnowledgeContextItem, 'title' | 'content' | 'source'>) {
  return `${item.title}::${item.content}::${item.source}`
}

export function normalizeKnowledgeContext(item: any): WorkflowKnowledgeContextItem | null {
  if (!item || typeof item !== 'object') {
    return null
  }

  const title = String(item.title || item.metadata?.project_title || item.source || '知识片段').trim()
  const content = String(item.content || '').trim()
  const source = String(item.source || 'knowledge').trim()

  if (!title || !content) {
    return null
  }

  return {
    id: String(item.id || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`),
    title,
    content,
    source,
    score: typeof item.score === 'number' ? item.score : null,
    created_at: item.created_at ? String(item.created_at) : null,
    metadata: item.metadata && typeof item.metadata === 'object' ? item.metadata : null,
  }
}

export function serializeKnowledgeContextsForWorkflow(items: WorkflowKnowledgeContextItem[]) {
  return items.map((item) => ({
    id: item.id,
    title: item.title,
    content: item.content,
    source: item.source,
    score: item.score,
    created_at: item.created_at,
    metadata: item.metadata || null,
  }))
}

export function mergeKnowledgeContexts(existing: WorkflowKnowledgeContextItem[], additions: WorkflowKnowledgeContextItem[]) {
  if (!additions.length) {
    return { changed: false, items: existing }
  }

  const merged = [...existing]
  const seen = new Set(merged.map((item) => getKnowledgeContextKey(item)))
  let changed = false

  for (const item of additions) {
    const normalized = normalizeKnowledgeContext(item)
    if (!normalized) {
      continue
    }

    const key = getKnowledgeContextKey(normalized)
    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    merged.push(normalized)
    changed = true
  }

  return { changed, items: merged }
}

export function getKnowledgeSourceLabel(source: string) {
  const labels: Record<string, string> = {
    script: '项目文本',
    scene: '分镜参考',
    character: '角色设定',
    knowledge: '素材片段',
    material: '图片素材',
    uploaded_user: '用户上传',
  }
  return labels[source] || source || '素材片段'
}

export function normalizeKnowledgeResult(result: KnowledgeSearchResult): WorkflowKnowledgeContextItem | null {
  return normalizeKnowledgeContext({
    title: String(result.metadata?.project_title || getKnowledgeSourceLabel(result.source)),
    content: result.content,
    source: result.source,
    score: result.score,
    created_at: result.created_at,
    metadata: result.metadata || null,
  })
}

export function buildKnowledgeBlock(items: WorkflowKnowledgeContextItem[]) {
  return items
    .map((item, index) => `素材参考 ${index + 1}【${item.title}】\n${item.content}`)
    .join('\n\n')
}

export function buildKnowledgeSearchQueryForScene(
  scene: StoryboardSceneDraft | null | undefined,
  fallbackQuery: string,
) {
  if (!scene) {
    return fallbackQuery.trim().slice(0, 240)
  }

  return [
    scene.location,
    scene.action,
    scene.dialogue_summary,
    scene.background_prompt,
  ].filter((item) => String(item || '').trim()).join(' ').slice(0, 240)
}